import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { HiX, HiShare, HiUser, HiSearch, HiCheck, HiShieldCheck } from "react-icons/hi";
import { getUsersAPI } from "../services/driveService";

const ShareModal = ({ isOpen, onClose, resource, onShare }) => {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [permission, setPermission] = useState("View");
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchUsers();
      setSelectedUser(null);
      setSearch("");
    }
  }, [isOpen]);

  const fetchUsers = async () => {
    setIsLoading(true);
    try {
      const response = await getUsersAPI();
      if (response.success) {
        setUsers(response.data);
      }
    } catch (error) {
      console.error("Failed to fetch users", error);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredUsers = users.filter(user => 
    user.fullName.toLowerCase().includes(search.toLowerCase()) || 
    user.email.toLowerCase().includes(search.toLowerCase())
  );

  const handleSubmit = async () => {
    if (!selectedUser) return;
    setIsSubmitting(true);
    await onShare(selectedUser.id, permission);
    setIsSubmitting(false);
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="relative w-full max-w-lg bg-white rounded-[2.5rem] shadow-2xl overflow-hidden"
          >
            <div className="p-8">
              {/* Header */}
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-4">
                   <div className="p-3 bg-indigo-50 text-indigo-600 rounded-2xl">
                     <HiShare size={24} />
                   </div>
                   <div>
                     <h3 className="text-xl font-extrabold text-slate-800">Share Item</h3>
                     <p className="text-xs font-bold text-slate-400 truncate max-w-[200px]">
                       {resource?.name}
                     </p>
                   </div>
                </div>
                <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-xl transition-colors">
                  <HiX size={20} className="text-slate-400" />
                </button>
              </div>

              {/* User Selection */}
              <div className="space-y-6">
                <div className="relative">
                  <HiSearch className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400 text-lg" />
                  <input
                    type="text"
                    placeholder="Search users by name or email..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full pl-14 pr-6 py-4 bg-slate-50 border border-slate-100 rounded-2xl outline-none focus:ring-4 focus:ring-indigo-500/10 focus:bg-white focus:border-indigo-200 transition-all font-bold text-slate-700"
                  />
                </div>

                <div className="max-h-60 overflow-y-auto pr-2 custom-scrollbar">
                  {isLoading ? (
                    <div className="flex flex-col items-center justify-center py-8 text-slate-400">
                      <div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin mb-4" />
                      <p className="font-bold text-sm">Loading users...</p>
                    </div>
                  ) : filteredUsers.length > 0 ? (
                    <div className="grid gap-2">
                       {filteredUsers.map(user => (
                         <div
                           key={user.id}
                           onClick={() => setSelectedUser(user)}
                           className={`flex items-center justify-between p-4 rounded-2xl cursor-pointer transition-all border-2 ${
                             selectedUser?.id === user.id 
                             ? "bg-indigo-50 border-indigo-200" 
                             : "bg-white border-transparent hover:bg-slate-50"
                           }`}
                         >
                           <div className="flex items-center gap-4">
                             <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-black ${
                               selectedUser?.id === user.id ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-400"
                             }`}>
                               {user.fullName.charAt(0)}
                             </div>
                             <div>
                               <p className="font-bold text-slate-700 text-sm leading-tight">{user.fullName}</p>
                               <p className="text-[10px] text-slate-400 font-bold">{user.email}</p>
                             </div>
                           </div>
                           {selectedUser?.id === user.id && <HiCheck className="text-indigo-600 text-xl" />}
                         </div>
                       ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-slate-400">
                      <HiUser size={40} className="mx-auto mb-2 opacity-20" />
                      <p className="font-bold">No users found</p>
                    </div>
                  )}
                </div>

                {/* Permissions */}
                <div className="p-6 bg-slate-50 rounded-[2rem] border border-slate-100">
                   <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4 ml-1">
                     Permission Level
                   </label>
                   <div className="flex gap-2">
                      {["View", "Edit"].map((p) => (
                        <button
                          key={p}
                          onClick={() => setPermission(p)}
                          className={`flex-1 py-3 px-6 rounded-xl font-bold text-sm transition-all flex items-center justify-center gap-2 ${
                            permission === p 
                            ? "bg-white text-indigo-600 shadow-md shadow-indigo-100" 
                            : "text-slate-400 hover:text-slate-600"
                          }`}
                        >
                          <HiShieldCheck className={permission === p ? "opacity-100" : "opacity-0"} />
                          {p}
                        </button>
                      ))}
                   </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-2">
                  <button
                    onClick={onClose}
                    className="flex-1 px-6 py-4 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-2xl font-bold transition-all active:scale-95 text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    disabled={!selectedUser || isSubmitting}
                    onClick={handleSubmit}
                    className="flex-[1.5] px-10 py-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-2xl font-bold shadow-lg shadow-indigo-100 transition-all active:scale-95 disabled:opacity-50 text-sm flex items-center justify-center gap-2"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Sharing...
                      </>
                    ) : (
                      "Grant Access"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default ShareModal;
